import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Loader2, Save, Star, Upload, X, Plus, Trash2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Field, Input, Textarea, Select, SectionTitle } from "@/components/FormBits";
import ChipGroup from "@/components/ChipGroup";
import CustomFieldsBlock from "@/components/CustomFieldsBlock";
import EnhanceableTextarea from "@/components/EnhanceableTextarea";
import EnhanceableInput from "@/components/EnhanceableInput";
import { ModePicker, CollapsedSection, ScorePill } from "@/components/AdaptiveBits";
import { setPendingSubmit } from "@/lib/pendingSubmits";
import { MODES, modeRank, FACT_CARDS, computeCaptureScore } from "@/config/adaptiveForm";
import { useWizardState } from "@/hooks/useWizardState";
import { api, fileUrl } from "@/lib/api";
import { WIZARD } from "@/constants/testIds";

const STEPS = [
  "Submitted By",
  "Project Details",
  "Project Summary",
  "The Challenge",
  "The Solution",
  "The Outcome",
  "Key Facts",
  "Photos & Files",
  "Final Notes & Submit",
];

const PROJECT_STATUSES = ["Completed", "In progress", "Upcoming"];
const UPLOAD_CATEGORIES = [
  "Before photos",
  "During works photos",
  "Completed works photos",
  "Drone photos",
  "Drawings or plans",
  "Videos",
  "Documents",
  "Other files",
];
const IMG_TAGS = ["Before", "During", "After", "Hero candidate", "Supporting image", "Private", "Do not use publicly"];
const DELIVERY_CHIPS = ["Delivered safely", "Delivered on time"];

export default function SubmitFull() {
  const { draftToken } = useParams();
  const navigate = useNavigate();
  const { project, loading, saving, lastSaved, update, saveNow } = useWizardState(draftToken);
  const [settings, setSettings] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [showModeModal, setShowModeModal] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  useEffect(() => {
    if (project?.current_step) setStep(project.current_step);
  }, [project?.id]);

  const mode = project?.detail_level || "full";
  const rank = modeRank(mode);
  const scoreInfo = useMemo(() => computeCaptureScore(project || {}), [project]);

  const REQUIRED_BY_STEP = {
    1: [["project_name", "Project name"], ["submitter_name", "Your name"], ["submitter_email", "Your email"]],
    2: [["client_name", "Client name"], ["location", "Location"]],
    3: [["one_sentence", "Short project summary"]],
  };

  const validateStep = (n) => {
    const reqs = REQUIRED_BY_STEP[n] || [];
    const missing = reqs.filter(([k]) => !((project?.[k] || "").trim()));
    return missing.map(([_, label]) => label);
  };

  const goTo = async (n) => {
    if (n > step) {
      const missing = validateStep(step);
      if (missing.length) {
        toast.error(`Please fill in: ${missing.join(", ")}`);
        return;
      }
    }
    await saveNow();
    update({ current_step: n });
    setStep(n);
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const onBack = () => step > 1 && goTo(step - 1);
  const onNext = () => step < STEPS.length && goTo(step + 1);
  const onSkip = () => onNext();

  const onSaveLater = async () => {
    await saveNow();
    try {
      const { data } = await api.post(`/projects/draft/${draftToken}/save-and-send-link`);
      toast.success("Draft saved", {
        description: project?.submitter_email
          ? `A private link has been emailed to ${project.submitter_email}.`
          : "You can return to this page using your private link.",
      });
      if (data?.draft_link) navigator.clipboard?.writeText(data.draft_link);
    } catch {
      toast.error("Could not send draft email — your draft is still saved.");
    }
  };

  const onSubmit = async () => {
    const missing = validateStep(1).concat(validateStep(2), validateStep(3));
    if (missing.length) {
      toast.error(`Please fill in: ${missing.join(", ")}`);
      return;
    }
    await saveNow();
    setSubmitting(true);
    localStorage.removeItem("rcw_draft_token");
    const submitPromise = api
      .post(`/projects/draft/${draftToken}/submit`, { hp_website: honeypot })
      .then(() => ({ ok: true }))
      .catch((e) => ({
        ok: false,
        detail: e?.response?.data?.detail || "Submission failed",
        status: e?.response?.status,
      }));
    setPendingSubmit(draftToken, submitPromise);
    navigate(`/thank-you?p=${draftToken}`);
  };

  const addOption = async (list, value) => {
    try {
      const { data } = await api.post(`/admin/settings/add-option`, { list, value });
      setSettings((s) => ({ ...(s || {}), [list]: data?.[list] || [...(s?.[list] || []), value] }));
    } catch {}
  };

  if (loading || !project || !settings) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin inline-block mr-2" /> Loading your draft…
        </div>
      </Layout>
    );
  }

  const pct = Math.round((step / STEPS.length) * 100);
  const stepProps = { project, update, settings, rank, addOption };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 rcw-mobile-pad-bottom">
        <input
          type="text"
          name="website"
          autoComplete="off"
          tabIndex={-1}
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}
          aria-hidden="true"
        />
        {/* Progress header */}
        <div className="rcw-card p-4 sm:p-5 mb-5 sm:mb-6 sticky top-16 z-20 bg-white">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="rcw-label" data-testid={WIZARD.stepLabel}>
                Step {step} of {STEPS.length}
              </div>
              <div className="font-display font-semibold text-base sm:text-lg text-gray-900 mt-0.5">
                {STEPS[step - 1]}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ScorePill {...scoreInfo} />
              <SaveBadge saving={saving} lastSaved={lastSaved} />
            </div>
          </div>
          <div className="mt-3 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden" data-testid={WIZARD.progressBar}>
            <div className="h-full bg-amber-500 transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
          {step > 1 && (
            <button
              type="button"
              onClick={() => setShowModeModal(true)}
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-amber-700 font-semibold"
              data-testid="change-detail-level"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              {MODES.find((m) => m.key === mode)?.label || "Full Detail"} mode · Change detail level
            </button>
          )}
        </div>

        {/* Step content */}
        <div className="rcw-card p-5 sm:p-8">
          {step === 1 && <Step1 {...stepProps} mode={mode} />}
          {step === 2 && <Step2 {...stepProps} />}
          {step === 3 && <Step3 {...stepProps} />}
          {step === 4 && <Step4 {...stepProps} />}
          {step === 5 && <Step5 {...stepProps} />}
          {step === 6 && <Step6 {...stepProps} />}
          {step === 7 && <Step7 {...stepProps} />}
          {step === 8 && <Step8 {...stepProps} draftToken={draftToken} />}
          {step === 9 && <Step9 {...stepProps} scoreInfo={scoreInfo} />}
          {rank >= 3 && step !== 1 && step !== 4 && step !== 8 && (
            <CustomFieldsBlock project={project} update={update} section={STEPS[step - 1]} />
          )}
        </div>
      </div>

      {/* Sticky bottom nav */}
      <div className="fixed sm:sticky bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 sm:mt-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex sm:hidden mb-2">
            {step < STEPS.length ? (
              <button onClick={onNext} className="rcw-btn-amber w-full text-base" data-testid={WIZARD.next}>
                Next <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={onSubmit} disabled={submitting} className="rcw-btn-amber w-full text-base" data-testid={WIZARD.submit}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Submit project
              </button>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <button onClick={onBack} disabled={step === 1} className="rcw-btn-ghost text-sm sm:text-base" data-testid={WIZARD.back}>
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <div className="flex items-center gap-2">
              <button onClick={onSaveLater} className="rcw-btn-ghost text-xs sm:text-base" data-testid={WIZARD.saveLater}>
                <Save className="h-4 w-4" /> <span className="hidden sm:inline">Save & continue later</span><span className="sm:hidden">Save</span>
              </button>
              <button onClick={onSkip} className="rcw-btn-ghost text-xs sm:text-sm" data-testid={WIZARD.skip}>
                Skip
              </button>
              {step < STEPS.length ? (
                <button onClick={onNext} className="hidden sm:inline-flex rcw-btn-amber text-base" data-testid={WIZARD.next + "-desktop"}>
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={onSubmit} disabled={submitting} className="hidden sm:inline-flex rcw-btn-amber text-base" data-testid={WIZARD.submit + "-desktop"}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Submit project
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showModeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowModeModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="mode-modal">
            <div className="font-display font-semibold text-lg text-gray-900 mb-1">Change detail level</div>
            <p className="text-xs text-gray-500 mb-4">
              This only shows or hides questions — nothing you've written is ever deleted.
            </p>
            <ModePicker
              value={mode}
              onChange={(m) => {
                update({ detail_level: m });
                setShowModeModal(false);
                toast.success(`${MODES.find((x) => x.key === m)?.label} mode`);
              }}
            />
          </div>
        </div>
      )}
    </Layout>
  );
}

function SaveBadge({ saving, lastSaved }) {
  return (
    <div
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
        saving ? "bg-gray-100 text-gray-600" : "bg-emerald-50 text-emerald-700"
      }`}
      data-testid={WIZARD.savedIndicator}
    >
      {saving ? (
        <>
          <Loader2 className="h-3 w-3 animate-spin" /> Saving…
        </>
      ) : (
        <>
          <Check className="h-3 w-3" /> Saved
          {lastSaved ? <span className="text-gray-400 hidden sm:inline">· {lastSaved.toLocaleTimeString()}</span> : null}
        </>
      )}
    </div>
  );
}

/* ----------- STEPS ----------- */

function Step1({ project, update, mode }) {
  return (
    <div className="space-y-6">
      <SectionTitle eyebrow="Submitted by" hint="Give it a name first — that's how it will appear in the library.">
        Name the project & tell us who you are
      </SectionTitle>
      <Field label="Project name" required hint="A simple internal name for the project.">
        <Input
          value={project.project_name || ""}
          onChange={(e) => update({ project_name: e.target.value })}
          placeholder="e.g. Valley Bridge"
          data-testid="step1-project-name"
        />
      </Field>
      <Field label="Your name" required>
        <Input
          value={project.submitter_name || ""}
          onChange={(e) => update({ submitter_name: e.target.value })}
          placeholder="e.g. Scott Williams"
          data-testid="step1-name"
        />
      </Field>
      <Field label="Your email address" required>
        <Input
          type="email"
          value={project.submitter_email || ""}
          onChange={(e) => update({ submitter_email: e.target.value })}
          placeholder="you@randcprojects.co.uk"
          data-testid="step1-email"
        />
      </Field>
      <div className="pt-4 border-t border-gray-100">
        <div className="rcw-label mb-1">How much time do you have?</div>
        <p className="text-xs text-gray-500 mb-3">
          You can change this at any time — your answers are always kept.
        </p>
        <ModePicker value={project.detail_level || "full"} onChange={(m) => update({ detail_level: m })} />
      </div>
    </div>
  );
}

function Step2({ project, update, settings, rank, addOption }) {
  const dateFields = (
    <>
      <Field label="Year completed">
        <Input value={project.year_completed || ""} onChange={(e) => update({ year_completed: e.target.value })} placeholder="e.g. 2025" data-testid="step2-year" />
      </Field>
      <Field label="Project value">
        <Input value={project.project_value || ""} onChange={(e) => update({ project_value: e.target.value })} placeholder="e.g. £450,000" data-testid="step2-value" />
      </Field>
      <Field label="Project start date">
        <Input type="date" value={project.project_start_date || ""} onChange={(e) => update({ project_start_date: e.target.value })} data-testid="step2-start-date" />
      </Field>
      <Field label="Project end date">
        <Input type="date" value={project.project_end_date || ""} onChange={(e) => update({ project_end_date: e.target.value })} data-testid="step2-end-date" />
      </Field>
    </>
  );

  return (
    <div className="space-y-6">
      <SectionTitle eyebrow="Project details">The basics</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Client name" required>
          <Input value={project.client_name || ""} onChange={(e) => update({ client_name: e.target.value })} placeholder="e.g. Leicestershire County Council" data-testid="step2-client" />
        </Field>
        <Field label="Location" required>
          <Input value={project.location || ""} onChange={(e) => update({ location: e.target.value })} placeholder="e.g. Mossdale Meadows, Braunstone" data-testid="step2-location" />
        </Field>
        <Field label="Project status">
          <Select options={PROJECT_STATUSES} value={project.project_status || ""} onChange={(e) => update({ project_status: e.target.value })} data-testid="step2-status" />
        </Field>
        {rank >= 2 && dateFields}
      </div>
      <Field label="Project category" hint="Choose any that apply.">
        <ChipGroup options={settings.categories} value={project.categories || []} onChange={(v) => update({ categories: v })} onAddOption={(v) => addOption("categories", v)} testIdPrefix="cat" />
      </Field>
      <Field label="Services delivered" hint="What R&C actually did on this job.">
        <ChipGroup options={settings.services || []} value={project.services_delivered || []} onChange={(v) => update({ services_delivered: v })} allowAdd={false} testIdPrefix="svc" />
      </Field>

      <CollapsedSection
        title="Add More Project Details"
        project={project}
        fieldKeys={["short_title", "additional_category_info", ...(rank < 2 ? ["year_completed", "project_value", "project_start_date", "project_end_date"] : [])]}
        testId="opt-project-details"
      >
        {rank < 2 && <div className="grid sm:grid-cols-2 gap-4">{dateFields}</div>}
        <Field label="Short project title, optional" hint="A short working title. Final headline options can be created later in the Outputs area.">
          <Input value={project.short_title || ""} onChange={(e) => update({ short_title: e.target.value })} placeholder="e.g. Bridge Deck Restoration" data-testid="step2-short-title" />
        </Field>
        <Field label="Additional project category information">
          <Textarea value={project.additional_category_info || ""} onChange={(e) => update({ additional_category_info: e.target.value })} placeholder="Anything else about the type of work?" data-testid="step2-extra-cat" />
        </Field>
      </CollapsedSection>
    </div>
  );
}

function Step3({ project, update, settings, rank }) {
  const ctx = `Project name: ${project.project_name || ""}. Client: ${project.client_name || ""}. Location: ${project.location || ""}. Categories: ${(project.categories || []).join(", ")}.`;
  const affectedField = (
    <Field label="Who was affected by the issue?">
      <ChipGroup options={settings.affected} value={project.who_affected || []} onChange={(v) => update({ who_affected: v })} testIdPrefix="affected" />
    </Field>
  );
  return (
    <div className="space-y-6">
      <SectionTitle eyebrow="Project summary">A short overview</SectionTitle>
      <Field label="In one or two sentences, what was the project?" required hint="Tell us what work was carried out and what it was intended to achieve.">
        <EnhanceableTextarea
          value={project.one_sentence}
          onChange={(e) => update({ one_sentence: e.target.value })}
          placeholder="e.g. A new culvert installation to reduce flood risk on the A6 near Braunstone."
          section="Project Summary"
          fieldLabel="Short project summary"
          contextHint={ctx}
          testId="step3-one-sentence"
        />
      </Field>
      {rank >= 2 && (
        <Field label="Why was the work needed?" hint="What problem did this project solve?">
          <EnhanceableTextarea
            value={project.why_needed}
            onChange={(e) => update({ why_needed: e.target.value })}
            placeholder="e.g. The existing structure had deteriorated and was at risk of failure during high-flow events."
            section="Project Summary"
            fieldLabel="Why was the work needed?"
            contextHint={ctx}
            testId="step3-why"
          />
        </Field>
      )}
      {rank >= 2 && affectedField}

      <CollapsedSection
        title="Add More Project Background"
        project={project}
        fieldKeys={["key_people", "additional_summary_info", ...(rank < 2 ? ["why_needed", "who_affected"] : [])]}
        testId="opt-background"
      >
        {rank < 2 && (
          <Field label="Why was the work needed?">
            <EnhanceableTextarea value={project.why_needed} onChange={(e) => update({ why_needed: e.target.value })} section="Project Summary" fieldLabel="Why was the work needed?" contextHint={ctx} testId="step3-why" />
          </Field>
        )}
        {rank < 2 && affectedField}
        <Field label="Who was involved in the project?" hint="R&C team, sub-contractors, partners, key stakeholders.">
          <EnhanceableTextarea value={project.key_people} onChange={(e) => update({ key_people: e.target.value })} placeholder="e.g. R&C site team, Wardell Armstrong (design)" section="Project Summary" fieldLabel="Who was involved" contextHint={ctx} testId="step3-people" />
        </Field>
        <Field label="Additional project background">
          <EnhanceableTextarea value={project.additional_summary_info} onChange={(e) => update({ additional_summary_info: e.target.value })} section="Project Summary" fieldLabel="Additional background" contextHint={ctx} testId="step3-extra" />
        </Field>
      </CollapsedSection>
    </div>
  );
}

function Step4({ project, update, settings, rank }) {
  const ctx = `${project.project_name || ""} for ${project.client_name || ""} at ${project.location || ""}`.trim();
  return (
    <div className="space-y-6">
      <SectionTitle eyebrow="The challenge">What made this project difficult or important?</SectionTitle>
      <Field label="Challenges" hint="Select all that apply.">
        <ChipGroup options={settings.challenges} value={project.challenges || []} onChange={(v) => update({ challenges: v })} allowAdd={false} testIdPrefix="chal" />
      </Field>
      {rank >= 2 && (
        <Field label="Describe the main challenge" hint="What made the project difficult, important or unusual?">
          <EnhanceableTextarea
            value={project.main_challenge_text}
            onChange={(e) => update({ main_challenge_text: e.target.value })}
            placeholder="e.g. The site sat next to an active watercourse with restricted access."
            section="The Challenge"
            fieldLabel="Main challenge"
            contextHint={ctx}
            testId="step4-main"
          />
        </Field>
      )}
      <CollapsedSection
        title="Add More Challenge Detail"
        project={project}
        fieldKeys={["other_challenge_text", "key_risks", "client_constraints", ...(rank < 2 ? ["main_challenge_text"] : [])]}
        testId="opt-challenge"
      >
        {rank < 2 && (
          <Field label="Describe the main challenge" hint="What made the project difficult, important or unusual?">
            <EnhanceableTextarea value={project.main_challenge_text} onChange={(e) => update({ main_challenge_text: e.target.value })} section="The Challenge" fieldLabel="Main challenge" contextHint={ctx} testId="step4-main" />
          </Field>
        )}
        <Field label="Additional challenge information">
          <EnhanceableTextarea value={project.other_challenge_text} onChange={(e) => update({ other_challenge_text: e.target.value })} section="The Challenge" fieldLabel="Additional challenge information" contextHint={ctx} testId="step4-other" />
        </Field>
        <Field label="Key risks">
          <EnhanceableTextarea value={project.key_risks} onChange={(e) => update({ key_risks: e.target.value })} placeholder="e.g. Flooding during construction, unexploded ordnance survey required." section="The Challenge" fieldLabel="Key risks" contextHint={ctx} testId="step4-risks" />
        </Field>
        <Field label="Constraints imposed by the client or location">
          <EnhanceableTextarea value={project.client_constraints} onChange={(e) => update({ client_constraints: e.target.value })} placeholder="e.g. Working hours restricted to 09:30–15:00; school access maintained at all times." section="The Challenge" fieldLabel="Client or location constraints" contextHint={ctx} testId="step4-constraints" />
        </Field>
      </CollapsedSection>
    </div>
  );
}

function Step5({ project, update, settings, rank, addOption }) {
  const ctx = `${project.project_name || ""} for ${project.client_name || ""} at ${project.location || ""}`.trim();
  return (
    <div className="space-y-6">
      <SectionTitle eyebrow="The solution">What did R&amp;C Williams do?</SectionTitle>
      <Field label="Works carried out" hint="Select all that apply.">
        <ChipGroup options={settings.solutions} value={project.solutions || []} onChange={(v) => update({ solutions: v })} onAddOption={(v) => addOption("solutions", v)} testIdPrefix="sol" />
      </Field>
      <Field label="Describe the works carried out" hint="Briefly explain what the team did.">
        <EnhanceableTextarea value={project.works_carried_out} onChange={(e) => update({ works_carried_out: e.target.value })} section="The Solution" fieldLabel="Works carried out" contextHint={ctx} testId="step5-works" />
      </Field>
      {rank >= 2 && (
        <>
          <Field label="How was the work delivered?" hint="For example, phasing, programme, work sequence, teams or access arrangements.">
            <EnhanceableTextarea value={project.how_delivered} onChange={(e) => update({ how_delivered: e.target.value })} placeholder="e.g. Delivered over 12 weeks in three phases with a night-works shift." section="The Solution" fieldLabel="How was the work delivered" contextHint={ctx} testId="step5-how" />
          </Field>
          <Field label="What made this project different?" hint="Something the client / industry / a bid team would want to hear.">
            <EnhanceableTextarea value={project.differentiator} onChange={(e) => update({ differentiator: e.target.value })} section="The Solution" fieldLabel="What made it different" contextHint={ctx} testId="step5-diff" />
          </Field>
        </>
      )}
      <CollapsedSection
        title="Add Technical Details"
        project={project}
        fieldKeys={[
          "materials_used", "specialist_methods", "temporary_works", "environmental_controls",
          "traffic_management_arrangements", "problems_solved", "innovation_used", "extra_solution",
          ...(rank < 2 ? ["how_delivered", "differentiator"] : []),
        ]}
        testId="opt-technical"
      >
        {rank < 2 && (
          <>
            <Field label="How was the work delivered?">
              <EnhanceableTextarea value={project.how_delivered} onChange={(e) => update({ how_delivered: e.target.value })} section="The Solution" fieldLabel="How was the work delivered" contextHint={ctx} testId="step5-how" />
            </Field>
            <Field label="What made this project different?">
              <EnhanceableTextarea value={project.differentiator} onChange={(e) => update({ differentiator: e.target.value })} section="The Solution" fieldLabel="What made it different" contextHint={ctx} testId="step5-diff" />
            </Field>
          </>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Materials used">
            <EnhanceableTextarea value={project.materials_used} onChange={(e) => update({ materials_used: e.target.value })} section="The Solution" fieldLabel="Materials used" contextHint={ctx} testId="step5-materials" />
          </Field>
          <Field label="Specialist methods, plant or equipment">
            <EnhanceableTextarea value={project.specialist_methods} onChange={(e) => update({ specialist_methods: e.target.value })} section="The Solution" fieldLabel="Specialist methods" contextHint={ctx} testId="step5-methods" />
          </Field>
          <Field label="Temporary works">
            <EnhanceableTextarea value={project.temporary_works} onChange={(e) => update({ temporary_works: e.target.value })} section="The Solution" fieldLabel="Temporary works" contextHint={ctx} testId="step5-temp" />
          </Field>
          <Field label="Environmental controls">
            <EnhanceableTextarea value={project.environmental_controls} onChange={(e) => update({ environmental_controls: e.target.value })} section="The Solution" fieldLabel="Environmental controls" contextHint={ctx} testId="step5-env" />
          </Field>
        </div>
        <Field label="Traffic management arrangements">
          <EnhanceableTextarea value={project.traffic_management_arrangements} onChange={(e) => update({ traffic_management_arrangements: e.target.value })} placeholder="e.g. Night-time contraflow with convoy working." section="The Solution" fieldLabel="Traffic management arrangements" contextHint={ctx} testId="step5-tm" />
        </Field>
        <Field label="Problems solved during delivery">
          <EnhanceableTextarea value={project.problems_solved} onChange={(e) => update({ problems_solved: e.target.value })} section="The Solution" fieldLabel="Problems solved" contextHint={ctx} testId="step5-problems" />
        </Field>
        <Field label="Innovation used" hint="Materials, tech, digital, drones, low-carbon methods, novel plant.">
          <EnhanceableTextarea value={project.innovation_used} onChange={(e) => update({ innovation_used: e.target.value })} section="The Solution" fieldLabel="Innovation used" contextHint={ctx} testId="step5-innovation" />
        </Field>
        <Field label="Additional solution details">
          <EnhanceableTextarea value={project.extra_solution} onChange={(e) => update({ extra_solution: e.target.value })} section="The Solution" fieldLabel="Additional solution details" contextHint={ctx} testId="step5-extra" />
        </Field>
      </CollapsedSection>
    </div>
  );
}

function Step6({ project, update, settings, rank, addOption }) {
  const ctx = `${project.project_name || ""} for ${project.client_name || ""} at ${project.location || ""}`.trim();
  const outcomeOptions = useMemo(() => {
    const base = settings.outcomes || [];
    return [...base, ...DELIVERY_CHIPS.filter((c) => !base.includes(c))];
  }, [settings.outcomes]);
  const selected = project.outcomes || [];
  return (
    <div className="space-y-6">
      <SectionTitle eyebrow="The outcome">What was achieved?</SectionTitle>
      <Field label="Outcomes" hint="Select all that apply.">
        <ChipGroup options={outcomeOptions} value={selected} onChange={(v) => update({ outcomes: v })} onAddOption={(v) => addOption("outcomes", v)} testIdPrefix="out" />
      </Field>
      {selected.includes("Delivered safely") && (
        <Field label="Delivered safely — add detail (optional)">
          <Textarea value={project.delivered_safely || ""} onChange={(e) => update({ delivered_safely: e.target.value })} placeholder="e.g. Zero RIDDOR incidents across 41,000 working hours." data-testid="step6-safe" />
        </Field>
      )}
      {selected.includes("Delivered on time") && (
        <Field label="Delivered on time — add detail (optional)">
          <Textarea value={project.delivered_on_time || ""} onChange={(e) => update({ delivered_on_time: e.target.value })} placeholder="e.g. Handed over three weeks ahead of programme." data-testid="step6-time" />
        </Field>
      )}
      <Field label="What was the main result?" hint="Plain, factual. AI Enhance can polish.">
        <EnhanceableTextarea
          value={project.main_result}
          onChange={(e) => update({ main_result: e.target.value })}
          placeholder="e.g. A new culvert with improved hydraulic capacity, reducing future flood risk."
          section="The Outcome"
          fieldLabel="Main result"
          contextHint={ctx}
          testId="step6-main"
        />
      </Field>
      {rank >= 2 && (
        <>
          <Field label="How did the client, public or end user benefit?">
            <EnhanceableTextarea value={project.client_public_benefit} onChange={(e) => update({ client_public_benefit: e.target.value })} section="The Outcome" fieldLabel="Client or public benefit" contextHint={ctx} testId="step6-benefit" />
          </Field>
          <Field label="Client feedback or quote" hint="Direct quotes or paraphrased feedback we can reuse.">
            <EnhanceableTextarea value={project.client_feedback} onChange={(e) => update({ client_feedback: e.target.value })} section="The Outcome" fieldLabel="Client feedback" contextHint={ctx} testId="step6-feedback" />
          </Field>
        </>
      )}
      <CollapsedSection
        title="Add More Outcome Detail"
        project={project}
        fieldKeys={["safety_improvements", "sustainability_benefits", "social_value", "extra_outcome", ...(rank < 2 ? ["client_public_benefit", "client_feedback"] : [])]}
        testId="opt-outcome"
      >
        {rank < 2 && (
          <>
            <Field label="How did the client, public or end user benefit?">
              <EnhanceableTextarea value={project.client_public_benefit} onChange={(e) => update({ client_public_benefit: e.target.value })} section="The Outcome" fieldLabel="Client or public benefit" contextHint={ctx} testId="step6-benefit" />
            </Field>
            <Field label="Client feedback or quote">
              <EnhanceableTextarea value={project.client_feedback} onChange={(e) => update({ client_feedback: e.target.value })} section="The Outcome" fieldLabel="Client feedback" contextHint={ctx} testId="step6-feedback" />
            </Field>
          </>
        )}
        <Field label="Safety improvements">
          <EnhanceableTextarea value={project.safety_improvements} onChange={(e) => update({ safety_improvements: e.target.value })} section="The Outcome" fieldLabel="Safety improvements" contextHint={ctx} testId="step6-safety-improve" />
        </Field>
        <Field label="Carbon or sustainability benefits">
          <EnhanceableTextarea value={project.sustainability_benefits} onChange={(e) => update({ sustainability_benefits: e.target.value })} section="The Outcome" fieldLabel="Sustainability benefits" contextHint={ctx} testId="step6-sustain" />
        </Field>
        <Field label="Social value delivered" hint="Local employment, community engagement, apprenticeships, charity, schools.">
          <EnhanceableTextarea value={project.social_value} onChange={(e) => update({ social_value: e.target.value })} section="The Outcome" fieldLabel="Social value" contextHint={ctx} testId="step6-social" />
        </Field>
        <Field label="Additional outcome information">
          <EnhanceableTextarea value={project.extra_outcome} onChange={(e) => update({ extra_outcome: e.target.value })} section="The Outcome" fieldLabel="Additional outcome information" contextHint={ctx} testId="step6-extra" />
        </Field>
      </CollapsedSection>
    </div>
  );
}

function Step7({ project, update, rank }) {
  const facts = project.custom_facts || [];
  const cards = FACT_CARDS.filter((f) => rank >= 3 || f.modes <= rank);
  const addFact = () => update({ custom_facts: [...facts, { id: crypto.randomUUID(), label: "", value: "", unit: "" }] });
  const updateFact = (id, patch) => update({ custom_facts: facts.map((f) => (f.id === id ? { ...f, ...patch } : f)) });
  const removeFact = (id) => update({ custom_facts: facts.filter((f) => f.id !== id) });

  return (
    <div className="space-y-5">
      <SectionTitle eyebrow="Key facts" hint="Complete the facts you know. You do not need to fill in every box.">
        Tell us the numbers
      </SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((f) => {
          const filled = !!(project[f.key] && String(project[f.key]).trim());
          return (
            <div key={f.key} className={`rounded-xl border p-3 transition ${filled ? "border-emerald-300 bg-emerald-50/40" : "border-gray-200 bg-white"}`} data-testid={`fact-card-${f.key}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{f.label}</div>
                {filled && (
                  <button type="button" onClick={() => update({ [f.key]: "" })} className="text-gray-400 hover:text-red-500" title="Clear" data-testid={`fact-clear-${f.key}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <input
                value={project[f.key] || ""}
                onChange={(e) => update({ [f.key]: e.target.value })}
                placeholder={f.ph}
                className="mt-1.5 w-full bg-transparent text-sm text-gray-900 outline-none border-0 focus:ring-0 px-0 placeholder:text-gray-400"
                data-testid={`step7-${f.key}`}
              />
            </div>
          );
        })}
      </div>

      <div className="pt-2">
        <div className="rcw-label mb-3">Custom facts</div>
        <div className="space-y-2">
          {facts.map((f) => (
            <div key={f.id} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center" data-testid={`custom-fact-${f.id}`}>
              <Input value={f.label} onChange={(e) => updateFact(f.id, { label: e.target.value })} placeholder="Fact label, e.g. Precast units installed" />
              <Input value={f.value} onChange={(e) => updateFact(f.id, { value: e.target.value })} placeholder="Value, e.g. 4" />
              <Input value={f.unit || ""} onChange={(e) => updateFact(f.id, { unit: e.target.value })} placeholder="Unit (optional)" className="sm:max-w-[120px]" />
              <button type="button" className="rcw-btn-ghost" onClick={() => removeFact(f.id)} aria-label="Remove fact">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addFact} className="mt-3 rcw-btn-ghost text-sm" data-testid="step7-add-fact">
          <Plus className="h-4 w-4" /> Add Custom Fact
        </button>
      </div>
    </div>
  );
}

function Step8({ project, update, draftToken, settings, rank }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState(UPLOAD_CATEGORIES[0]);
  const files = (project.files || []).filter((f) => !f.is_deleted);
  const images = files.filter((f) => (f.content_type || "").startsWith("image/"));
  const accepted = settings.accepted_extensions.map((e) => "." + e).join(",");

  const handleFiles = async (fileList) => {
    setBusy(true);
    try {
      const accum = [...(project.files || [])];
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category);
        try {
          const { data } = await api.post(`/projects/draft/${draftToken}/files`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          accum.push(data);
          update({ files: [...accum] });
        } catch (e) {
          const msg = e?.response?.data?.detail || "Upload failed";
          toast.error(typeof msg === "string" ? msg : "Upload failed");
        }
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const updateFile = async (path, patch) => {
    update({ files: (project.files || []).map((f) => (f.storage_path === path ? { ...f, ...patch } : f)) });
    try {
      await api.patch(`/projects/draft/${draftToken}/files/${path}`, patch);
    } catch {}
  };

  const removeFile = async (path) => {
    update({ files: (project.files || []).map((f) => (f.storage_path === path ? { ...f, is_deleted: true } : f)) });
    try {
      await api.delete(`/projects/draft/${draftToken}/files/${path}`);
    } catch {}
  };

  return (
    <div className="space-y-5">
      <SectionTitle eyebrow="Photos & files" hint={rank < 2 ? "Photos are the most valuable thing you can add — even more than long answers." : "Drop photos straight from your phone. Captions and tags are optional."}>
        Upload photos and files
      </SectionTitle>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <Select options={UPLOAD_CATEGORIES} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="step8-upload-category" />
        <label className="rcw-btn-amber cursor-pointer sm:flex-none w-full sm:w-auto" data-testid="step8-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {busy ? "Uploading…" : "Choose files"}
          <input ref={inputRef} type="file" multiple accept={accepted} className="hidden" onChange={(e) => handleFiles(e.target.files)} />
        </label>
        <div className="text-xs text-gray-500">
          Max {settings.max_file_size_mb} MB per file · {settings.accepted_extensions.join(", ").toUpperCase()}
        </div>
      </div>

      {images.length > 0 && (
        <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Star className="h-3.5 w-3.5 inline text-amber-500 mr-1" />
          Which image best represents the project? Tap the star on your best photo.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {files.map((f) => {
          const isImage = (f.content_type || "").startsWith("image/");
          const isBest = project.best_image_path === f.storage_path;
          return (
            <div key={f.storage_path} className={`rcw-card overflow-hidden ${isBest ? "ring-2 ring-amber-400" : ""}`} data-testid={`file-${f.id}`}>
              <div className="aspect-video bg-gray-100 relative">
                {isImage ? (
                  <img src={fileUrl(f.storage_path)} alt={f.original_filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs px-2 text-center">
                    {f.original_filename}
                  </div>
                )}
                <button
                  onClick={() => removeFile(f.storage_path)}
                  className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 text-white inline-flex items-center justify-center"
                  data-testid={`file-remove-${f.id}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                {isImage && (
                  <button
                    onClick={() => update({ best_image_path: isBest ? "" : f.storage_path })}
                    className={`absolute top-1.5 left-1.5 h-7 w-7 rounded-full inline-flex items-center justify-center shadow-sm ${isBest ? "bg-amber-400 text-white" : "bg-white/85 text-gray-500 hover:text-amber-500"}`}
                    title="Best project image"
                    data-testid={`file-best-${f.id}`}
                  >
                    <Star className={`h-4 w-4 ${isBest ? "fill-white" : ""}`} />
                  </button>
                )}
              </div>
              <div className="p-2.5 space-y-2">
                <input
                  value={f.caption || ""}
                  placeholder="Caption…"
                  className="text-sm w-full bg-transparent outline-none border-0 focus:ring-0 px-0"
                  onChange={(e) => updateFile(f.storage_path, { caption: e.target.value })}
                />
                {isImage && (
                  <div className="flex flex-wrap gap-1">
                    {IMG_TAGS.map((t) => {
                      const sel = (f.tags || []).includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          className="text-[10px] px-2 py-0.5 rounded-full border"
                          style={{
                            background: sel ? "#111827" : "#fff",
                            color: sel ? "#fff" : "#4B5563",
                            borderColor: sel ? "#111827" : "#E5E7EB",
                          }}
                          onClick={() =>
                            updateFile(f.storage_path, {
                              tags: sel ? (f.tags || []).filter((x) => x !== t) : [...(f.tags || []), t],
                            })
                          }
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="text-[10px] text-gray-400">{f.category}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Step9({ project, update, rank, scoreInfo }) {
  const ctx = `${project.project_name || ""} for ${project.client_name || ""} at ${project.location || ""}`.trim();
  const files = (project.files || []).filter((f) => !f.is_deleted);
  return (
    <div className="space-y-6">
      <SectionTitle eyebrow="Final notes">Anything to add before we wrap up?</SectionTitle>
      {rank >= 2 && (
        <>
          <Field label="What are you most proud of on this project?">
            <EnhanceableTextarea value={project.proud_of} onChange={(e) => update({ proud_of: e.target.value })} section="Final Notes" fieldLabel="Most proud of" contextHint={ctx} testId="step9-proud" />
          </Field>
          <Field label="Anything unusual or worth mentioning?">
            <EnhanceableTextarea value={project.unusual_impressive} onChange={(e) => update({ unusual_impressive: e.target.value })} section="Final Notes" fieldLabel="Unusual or impressive" contextHint={ctx} testId="step9-unusual" />
          </Field>
        </>
      )}
      <CollapsedSection
        title="Add Final Information"
        project={project}
        fieldKeys={["lessons_learned", "avoid_publicly", "client_approval_notes", "internal_notes", ...(rank < 2 ? ["proud_of", "unusual_impressive"] : [])]}
        testId="opt-final"
      >
        {rank < 2 && (
          <>
            <Field label="What are you most proud of on this project?">
              <EnhanceableTextarea value={project.proud_of} onChange={(e) => update({ proud_of: e.target.value })} section="Final Notes" fieldLabel="Most proud of" contextHint={ctx} testId="step9-proud" />
            </Field>
            <Field label="Anything unusual or worth mentioning?">
              <EnhanceableTextarea value={project.unusual_impressive} onChange={(e) => update({ unusual_impressive: e.target.value })} section="Final Notes" fieldLabel="Unusual or impressive" contextHint={ctx} testId="step9-unusual" />
            </Field>
          </>
        )}
        <Field label="Lessons learned" hint="Anything we'd do differently next time — or want the wider team to know.">
          <EnhanceableTextarea value={project.lessons_learned} onChange={(e) => update({ lessons_learned: e.target.value })} section="Final Notes" fieldLabel="Lessons learned" contextHint={ctx} testId="step9-lessons" />
        </Field>
        <Field label="Anything we should avoid saying publicly?">
          <Textarea value={project.avoid_publicly || ""} onChange={(e) => update({ avoid_publicly: e.target.value })} data-testid="step9-avoid" />
        </Field>
        <Field label="Client approval notes">
          <Textarea value={project.client_approval_notes || ""} onChange={(e) => update({ client_approval_notes: e.target.value })} placeholder="e.g. Client comms team must sign off any photography." data-testid="step9-approval-notes" />
        </Field>
        <Field label="Internal notes">
          <Textarea value={project.internal_notes || ""} onChange={(e) => update({ internal_notes: e.target.value })} placeholder="Not shown publicly — for the R&C team only." data-testid="step9-internal" />
        </Field>
      </CollapsedSection>

      <Field label="Can this project information be used publicly?">
        <Select options={["Yes", "No", "Check with the client first"]} value={project.public_use || ""} onChange={(e) => update({ public_use: e.target.value })} data-testid="step9-public" />
      </Field>

      {/* Confirmation summary */}
      <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4" data-testid="submit-summary">
        <div className="text-[11px] uppercase tracking-widest text-gray-400 font-bold mb-2">Ready to submit</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <div className="text-gray-500">Project</div>
          <div className="font-semibold text-gray-900">{project.project_name || "—"}</div>
          <div className="text-gray-500">Client</div>
          <div className="font-semibold text-gray-900">{project.client_name || "—"}</div>
          <div className="text-gray-500">Location</div>
          <div className="font-semibold text-gray-900">{project.location || "—"}</div>
          <div className="text-gray-500">Photos & files</div>
          <div className="font-semibold text-gray-900">{files.length}</div>
          <div className="text-gray-500">Capture score</div>
          <div className="font-semibold text-gray-900">{scoreInfo.score}% · {scoreInfo.status}</div>
        </div>
        {scoreInfo.suggestions.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-[11px] uppercase tracking-widest text-gray-400 font-bold mb-1.5">Optional improvements</div>
            <ul className="space-y-1">
              {scoreInfo.suggestions.map((s) => (
                <li key={s} className="text-xs text-gray-600 flex gap-1.5"><span className="text-amber-500 font-bold">→</span> {s}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-3 text-xs text-gray-400">You can submit at any score — every record is useful.</div>
      </div>
    </div>
  );
}
